<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { useProjectsStore, type Project } from '@/stores/projects'
import { ElMessageBox, ElMessage } from 'element-plus'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const projectsStore = useProjectsStore()

const showNewProject = ref(false)
const user = computed(() => authStore.user)
const credits = computed(() => authStore.credits)
const projects = computed(() => projectsStore.projects)
const loading = computed(() => projectsStore.loading)
const activeProjects = computed(() => projectsStore.activeProjects)

// Handle new project button click
async function handleNewProject() {
  // Check if user has an active project
  const { hasActive, activeProject } = await projectsStore.checkHasActiveProject()
  
  if (hasActive && activeProject) {
    // Show popup warning
    ElMessageBox.confirm(
      t('project.active_warning.message', { title: activeProject.title }),
      t('project.active_warning.title'),
      {
        confirmButtonText: t('project.active_warning.view_project'),
        cancelButtonText: t('common.cancel'),
        type: 'warning',
        center: true
      }
    ).then(() => {
      // Go to active project
      router.push(`/project/${activeProject.id}`)
    }).catch(() => {
      // User cancelled, do nothing
    })
  } else {
    // No active project, proceed to create
    router.push('/project/new')
  }
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    draft: 'info',
    pending: 'warning',
    processing: 'warning',
    script_ready: 'primary',
    images_ready: 'primary',
    videos_ready: 'primary',
    completed: 'success',
    failed: 'danger',
    cancelled: 'info'
  }
  return colors[status] || 'info'
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function viewProject(project: Project) {
  router.push(`/project/${project.id}`)
}

onMounted(async () => {
  // Check if returning from successful payment
  if (route.query.payment === 'success') {
    ElMessage.success(t('dashboard.payment_success'))
    // Refresh user data to get updated credits
    await authStore.fetchUser()
    // Clean URL
    router.replace({ path: '/dashboard', query: {} })
  }
  
  projectsStore.fetchProjects()
  
  // Poll for active projects
  if (activeProjects.value.length > 0) {
    const interval = setInterval(() => {
      projectsStore.fetchProjects()
    }, 5000)
    
    onUnmounted(() => clearInterval(interval))
  }
})
</script>

<template>
  <div class="dashboard-view">
    <div class="dashboard-header">
      <div class="header-left">
        <h1>{{ t('dashboard.title') }}</h1>
        <p>{{ t('dashboard.welcome') }}, {{ user?.display_name || user?.email }}</p>
      </div>
      <div class="header-right">
        <div class="credits-display">
          <span class="credits-icon">💎</span>
          <div class="credits-info">
            <span class="credits-label">{{ t('dashboard.credits') }}</span>
            <span class="credits-value">{{ credits }}</span>
          </div>
        </div>
        <button class="new-project-btn" @click="handleNewProject">
          <span>+</span>
          {{ t('dashboard.new_project') }}
        </button>
      </div>
    </div>

    <!-- Active Projects Alert -->
    <el-alert 
      v-if="activeProjects.length > 0"
      type="info"
      :closable="false"
      class="active-alert"
    >
      <template #title>
        <span class="active-alert-content">
          <span class="pulse-dot"></span>
          {{ activeProjects.length }} projet(s) en cours de traitement
        </span>
      </template>
    </el-alert>

    <div class="dashboard-content">
      <h2 class="section-title">{{ t('dashboard.projects') }}</h2>

      <!-- Loading -->
      <div v-if="loading" class="projects-loading">
        <el-skeleton :rows="3" animated />
      </div>

      <!-- Empty State -->
      <div v-else-if="projects.length === 0" class="projects-empty">
        <div class="empty-icon">🎬</div>
        <h3>{{ t('dashboard.no_projects') }}</h3>
        <p>{{ t('dashboard.create_first') }}</p>
        <button class="new-project-btn primary" @click="handleNewProject">
          <span>+</span>
          {{ t('dashboard.new_project') }}
        </button>
      </div>

      <!-- Projects Grid -->
      <div v-else class="projects-grid">
        <div 
          v-for="project in projects" 
          :key="project.id"
          class="project-card"
          @click="viewProject(project)"
        >
          <div class="card-thumbnail">
            <img 
              v-if="project.thumbnail_url" 
              :src="project.thumbnail_url" 
              :alt="project.title"
            />
            <div v-else class="thumbnail-placeholder">
              <span>🎬</span>
            </div>
            
            <!-- Progress overlay for active projects -->
            <div v-if="project.progress > 0 && project.progress < 100" class="progress-overlay">
              <el-progress 
                type="circle" 
                :percentage="project.progress"
                :width="60"
              />
            </div>
          </div>
          
          <div class="card-content">
            <div class="card-header">
              <h3 class="card-title">{{ project.title }}</h3>
              <el-tag :type="getStatusColor(project.status)" size="small">
                {{ t(`status.${project.status}`) }}
              </el-tag>
            </div>
            
            <div class="card-meta">
              <span class="meta-item">
                <span class="meta-icon">📅</span>
                {{ formatDate(project.created_at) }}
              </span>
              <span class="meta-item">
                <span class="meta-icon">🎬</span>
                {{ project.format }}
              </span>
              <span v-if="project.credits_used > 0" class="meta-item">
                <span class="meta-icon">💎</span>
                {{ project.credits_used }}
              </span>
            </div>
            
            <div v-if="project.visual_style" class="card-style">
              {{ project.visual_style }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard-view {
  min-height: calc(100vh - 64px);
  padding: var(--m2v-space-8) var(--m2v-space-6);
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--m2v-space-8);
  flex-wrap: wrap;
  gap: var(--m2v-space-4);
}

.header-left h1 {
  font-family: var(--m2v-font-display);
  font-size: 2rem;
  margin-bottom: var(--m2v-space-2);
}

.header-left p {
  color: var(--m2v-text-muted);
}

.header-right {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-4);
}

.credits-display {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
  padding: var(--m2v-space-3) var(--m2v-space-5);
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
}

.credits-icon {
  font-size: 1.5rem;
}

.credits-info {
  display: flex;
  flex-direction: column;
}

.credits-label {
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
}

.credits-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.new-project-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--m2v-space-2);
  padding: var(--m2v-space-3) var(--m2v-space-5);
  background: var(--m2v-primary);
  color: white;
  font-weight: 600;
  border-radius: var(--m2v-radius-lg);
  transition: all var(--m2v-transition-fast);
}

.new-project-btn:hover {
  background: var(--m2v-primary-dark);
  color: white;
}

.new-project-btn.primary {
  padding: var(--m2v-space-4) var(--m2v-space-6);
}

.active-alert {
  margin-bottom: var(--m2v-space-6);
}

.active-alert-content {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-2);
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background: var(--m2v-primary);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

.section-title {
  font-size: 1.25rem;
  margin-bottom: var(--m2v-space-6);
}

.projects-loading {
  padding: var(--m2v-space-8);
}

.projects-empty {
  text-align: center;
  padding: var(--m2v-space-12);
  background: var(--m2v-bg-surface);
  border: 1px dashed var(--m2v-border-default);
  border-radius: var(--m2v-radius-xl);
}

.projects-empty .empty-icon {
  font-size: 4rem;
  margin-bottom: var(--m2v-space-4);
}

.projects-empty h3 {
  margin-bottom: var(--m2v-space-2);
}

.projects-empty p {
  color: var(--m2v-text-muted);
  margin-bottom: var(--m2v-space-6);
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: var(--m2v-space-6);
}

.project-card {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  overflow: hidden;
  cursor: pointer;
  transition: all var(--m2v-transition-normal);
}

.project-card:hover {
  transform: translateY(-4px);
  border-color: var(--m2v-primary);
  box-shadow: var(--m2v-shadow-lg);
}

.card-thumbnail {
  position: relative;
  aspect-ratio: 16/9;
  background: var(--m2v-bg-elevated);
}

.card-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
}

.progress-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
}

.card-content {
  padding: var(--m2v-space-4);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--m2v-space-2);
  margin-bottom: var(--m2v-space-3);
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--m2v-space-3);
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-1);
}

.card-style {
  margin-top: var(--m2v-space-3);
  padding: var(--m2v-space-2) var(--m2v-space-3);
  background: var(--m2v-bg-elevated);
  border-radius: var(--m2v-radius-md);
  font-size: 0.75rem;
  color: var(--m2v-text-secondary);
}
</style>
